/**
 * Gestão de Empresas - Super Admin
 * 
 * Lista todas as empresas com métricas e detalhes individuais
 */

import { useState, useEffect, useMemo } from 'react';
import { tenantsApi } from '../../services/superadmin/tenantsApiService';
import type {
  TenantListItem,
  TenantsFilters,
  TenantDetails,
  TenantTimelineDataPoint
} from '../../types/superadmin/tenant.types';

// 🔧 v1.4.9: Recharts comentado temporariamente (dependência não instalada no volume Docker)
// import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { SuperAdminNav } from '../../components/SuperAdminNav';

export const SuperAdminTenantsPage = () => {
  console.log(`%c[PAI Layout] SuperAdminTenantsPage Renderizou`, 'color: #ffaa00; font-weight: bold;');
  const [tenants, setTenants] = useState<TenantListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Paginação e filtros
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState<TenantsFilters>({
    page: 1,
    limit: 20
  });

  // Modal de detalhes
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null);
  const [tenantDetails, setTenantDetails] = useState<TenantDetails | null>(null);
  const [tenantTimeline, setTenantTimeline] = useState<TenantTimelineDataPoint[]>([]);
  const [loadingDetails, setLoadingDetails] = useState(false);

  useEffect(() => {
    loadTenants();
  }, [filters]);

  // Extrair plataformas únicas dos tenants carregados
  const uniquePlatforms = useMemo(() => {
    const platforms = tenants
      .map((t: TenantListItem) => t.plataforma)
      .filter((p): p is string => !!p && p.trim() !== '');
    return [...new Set(platforms)].sort((a, b) => a.localeCompare(b));
  }, [tenants]);

  const loadTenants = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await tenantsApi.getTenants(filters);
      
      setTenants(data.tenants);
      setCurrentPage(data.pagination.page);
      setTotalPages(data.pagination.pages);
    } catch (err: any) {
      console.error('Erro ao carregar empresas:', err);
      setError(err.message || 'Erro ao carregar empresas');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key: keyof TenantsFilters, value: any) => {
    setFilters((prev: TenantsFilters) => ({
      ...prev,
      [key]: value,
      page: 1 // Reset para página 1 ao filtrar
    }));
  };

  const handlePageChange = (newPage: number) => {
    setFilters((prev: TenantsFilters) => ({ ...prev, page: newPage }));
  };

  const openTenantDetails = async (tenantId: string) => {
    try {
      setSelectedTenantId(tenantId);
      setLoadingDetails(true);

      const [details, timeline] = await Promise.all([
        tenantsApi.getTenantDetails(tenantId),
        tenantsApi.getTenantDispatchesTimeline(tenantId, 30)
      ]);

      setTenantDetails(details);
      setTenantTimeline(timeline.timeline);
    } catch (err: any) {
      console.error('Erro ao carregar detalhes:', err);
      alert('Erro ao carregar detalhes da empresa: ' + err.message);
      setSelectedTenantId(null);
    } finally {
      setLoadingDetails(false);
    }
  };

  const closeTenantDetails = () => {
    setSelectedTenantId(null);
    setTenantDetails(null);
    setTenantTimeline([]);
  };

  const getStatusBadge = (status: string) => {
    if (status === 'active') {
      return <span className="px-2 py-1 text-xs font-semibold text-green-800 bg-green-100 rounded-full">✅ Ativo</span>;
    }
    return <span className="px-2 py-1 text-xs font-semibold text-gray-800 bg-gray-100 rounded-full">⚪ Inativo</span>;
  };

  const getLimitColor = (percentage: number) => {
    if (percentage >= 80) return 'text-red-600';
    if (percentage >= 50) return 'text-yellow-600';
    return 'text-green-600';
  };

  if (loading && tenants.length === 0) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando empresas...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <SuperAdminNav />
      <div className="container mx-auto p-6">
        {/* MODIFICADO: Header - adaptado para tema dark */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>Gestão de Empresas</h1>
          <p className="mt-2" style={{ color: 'var(--text-secondary)' }}>Lista de todas as empresas com métricas e detalhes</p>
        </div>

      {/* MODIFICADO: Filtros - adaptado para tema dark */}
      <div className="rounded-lg shadow p-6 mb-6" style={{ backgroundColor: 'var(--bg-card)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}>
        <h2 className="text-lg font-bold mb-4" style={{ color: 'var(--text-primary)' }}>🔍 Filtros</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Busca */}
          {/* MODIFICADO: input - adaptado para tema dark */}
          <input
            type="text"
            placeholder="Buscar por nome..."
            className="px-4 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            style={{ backgroundColor: 'var(--bg-card)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}
            value={filters.search || ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFilterChange('search', e.target.value)}
          />

          {/* Status */}
          {/* MODIFICADO: select - adaptado para tema dark */}
          <select
            className="px-4 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            style={{ backgroundColor: 'var(--bg-card)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}
            value={filters.status || ''}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleFilterChange('status', e.target.value as any)}
          >
            <option value="">Todos os status</option>
            <option value="active">Ativos</option>
            <option value="inactive">Inativos</option>
          </select>

          {/* Plataforma */}
          {/* MODIFICADO: dropdown plataforma - lista dinâmica das plataformas cadastradas */}
          <select
            className="px-4 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            style={{ backgroundColor: 'var(--bg-card)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}
            value={filters.platform || ''}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleFilterChange('platform', e.target.value)}
          >
            <option value="">Todas as plataformas</option>
            {uniquePlatforms.map((platform) => (
              <option key={platform} value={platform}>{platform}</option>
            ))}
          </select>

          {/* Botão Limpar */}
          {/* MODIFICADO: botão limpar - adaptado para tema dark */}
          <button
            onClick={() => setFilters({ page: 1, limit: 20 })}
            className="px-4 py-2 rounded transition-colors"
            style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}
          >
            Limpar Filtros
          </button>
        </div>
      </div>

      {/* Tabela */}
      {error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
          ❌ {error}
          <button onClick={loadTenants} className="ml-4 text-blue-600 underline">
            Tentar Novamente
          </button>
        </div>
      ) : (
        <>
        {/* MODIFICADO: Tabela - adaptada para tema dark */}
        <div className="rounded-lg shadow overflow-hidden" style={{ backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-color)' }}>
          <table className="min-w-full divide-y" style={{ borderColor: 'var(--border-color)' }}>
            <thead style={{ backgroundColor: 'var(--bg-secondary)' }}>
              <tr>
                {['Empresa','Status','Plataforma','Números WW','Disparos (Mês)','Limite','Campanhas','Ações'].map(h => (
                  <th key={h} className="px-6 py-3 text-left text-sm font-semibold uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y" style={{ borderColor: 'var(--border-color)' }}>
              {tenants.map((tenant: TenantListItem) => (
                <tr
                  key={tenant.id}
                  className="transition-colors"
                  style={{ backgroundColor: 'transparent' }}
                  onMouseEnter={(e: React.MouseEvent<HTMLTableRowElement>) => { (e.currentTarget as HTMLTableRowElement).style.backgroundColor = 'var(--bg-secondary)'; }}
                  onMouseLeave={(e: React.MouseEvent<HTMLTableRowElement>) => { (e.currentTarget as HTMLTableRowElement).style.backgroundColor = 'transparent'; }}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{tenant.name}</div>
                    <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>{tenant.id.substring(0, 8)}...</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(tenant.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm" style={{ color: 'var(--text-primary)' }}>{tenant.plataforma || '-'}</div>
                    <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>{tenant.idPlataforma || '-'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text-primary)' }}>
                    <span className="text-green-600">{tenant.metrics.whatsappWebSessions.connected}</span> / {tenant.metrics.whatsappWebSessions.total}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text-primary)' }}>
                    {tenant.metrics.dispatchesThisMonth.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className={`text-sm font-semibold ${getLimitColor(tenant.metrics.limit.percentage)}`}>
                      {tenant.metrics.limit.percentage.toFixed(1)}%
                    </div>
                    <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                      {tenant.metrics.limit.used.toLocaleString()} / {tenant.metrics.limit.total.toLocaleString()}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text-primary)' }}>
                    {tenant.metrics.campaignsActive}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <button
                      onClick={() => openTenantDetails(tenant.id)}
                      className="text-blue-600 hover:text-blue-900 font-medium"
                    >
                      Ver Detalhes
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Paginação */}
          {totalPages > 1 && (
            <div className="px-6 py-3 flex items-center justify-between border-t" style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}>
              <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                Página {currentPage} de {totalPages}
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  style={{ backgroundColor: 'var(--bg-card)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}
                >
                  Anterior
                </button>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  style={{ backgroundColor: 'var(--bg-card)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}
                >
                  Próxima
                </button>
              </div>
            </div>
          )}
        </div>
        </>
      )}

      {/* Modal de Detalhes */}
      {selectedTenantId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          {/* MODIFICADO: Modal - adaptado para tema dark */}
          <div className="rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto" style={{ backgroundColor: 'var(--bg-card)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}>
            {/* Header do Modal */}
            <div className="sticky top-0 px-6 py-4 flex justify-between items-center border-b" style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)' }}>
              <h2 className="text-xl font-semibold text-n-slate-12">
                {tenantDetails?.tenant.name || 'Carregando...'}
              </h2>
              <button
                onClick={closeTenantDetails}
                className="text-2xl"
                style={{ color: 'var(--text-secondary)' }}
              >
                ×
              </button>
            </div>

            {/* Conteúdo do Modal */}
            <div className="p-6">
              {loadingDetails ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-4" style={{ color: 'var(--text-secondary)' }}>Carregando detalhes...</p>
                </div>
              ) : tenantDetails ? (
                <div className="space-y-6">
                  {/* Informações Básicas */}
                  <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--bg-secondary)' }}>
                    <h3 className="text-lg font-bold mb-3" style={{ color: 'var(--text-primary)' }}>📄 Informações Básicas</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>Status:</span>
                        <div className="mt-1">{getStatusBadge(tenantDetails.tenant.status)}</div>
                      </div>
                      <div>
                        <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>Plataforma:</span>
                        <div className="mt-1 text-sm font-medium">{tenantDetails.tenant.plataforma || '-'}</div>
                      </div>
                      <div>
                        <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>ID Plataforma:</span>
                        <div className="mt-1 text-sm font-medium">{tenantDetails.tenant.idPlataforma || '-'}</div>
                      </div>
                      <div>
                        <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>Criado em:</span>
                        <div className="mt-1 text-sm font-medium">
                          {new Date(tenantDetails.tenant.createdAt).toLocaleString('pt-BR')}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Limite de Disparos */}
                  <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--bg-secondary)' }}>
                    <h3 className="text-lg font-bold mb-3" style={{ color: 'var(--text-primary)' }}>🚦 Limites de Disparos</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between text-sm">
                        <span style={{ color: 'var(--text-secondary)' }}>Limite Mensal:</span>
                        <span className="font-semibold">{tenantDetails.dispatchLimits.monthlyLimit.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span style={{ color: 'var(--text-secondary)' }}>Usado:</span>
                        <span className="font-semibold">{tenantDetails.dispatchLimits.used.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span style={{ color: 'var(--text-secondary)' }}>Disponível:</span>
                        <span className="font-semibold text-green-600">{tenantDetails.dispatchLimits.available.toLocaleString()}</span>
                      </div>
                      <div className="mt-2">
                        <div className="flex justify-between text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>
                          <span>Uso:</span>
                          <span>{tenantDetails.dispatchLimits.percentage.toFixed(1)}%</span>
                        </div>
                        <div className="w-full rounded-full h-2" style={{ backgroundColor: 'var(--border-color)' }}>
                          <div 
                            className={`h-2 rounded-full transition-all ${
                              tenantDetails.dispatchLimits.percentage >= 80 ? 'bg-red-600' :
                              tenantDetails.dispatchLimits.percentage >= 50 ? 'bg-yellow-600' : 'bg-green-600'
                            }`}
                            style={{ width: `${Math.min(tenantDetails.dispatchLimits.percentage, 100)}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* MODIFICADO: Estatísticas - adaptado para tema dark */}
                  {/* ANTES: bg-blue-50/bg-green-50 e textos fixos */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}>
                      <h4 className="font-bold mb-3" style={{ color: 'var(--text-primary)' }}>📱 WhatsApp Web</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span style={{ color: 'var(--text-secondary)' }}>Total:</span>
                          <span className="font-semibold">{tenantDetails.statistics.whatsappWeb.total.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span style={{ color: 'var(--text-secondary)' }}>Este mês:</span>
                          <span className="font-semibold">{tenantDetails.statistics.whatsappWeb.thisMonth.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span style={{ color: 'var(--text-secondary)' }}>Taxa de sucesso:</span>
                          <span className="font-semibold text-green-600">{tenantDetails.statistics.whatsappWeb.successRate.toFixed(1)}%</span>
                        </div>
                      </div>
                    </div>

                    <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}>
                      <h4 className="font-bold mb-3" style={{ color: 'var(--text-primary)' }}>🎯 API Oficial</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span style={{ color: 'var(--text-secondary)' }}>Total:</span>
                          <span className="font-semibold">{tenantDetails.statistics.officialApi.total.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span style={{ color: 'var(--text-secondary)' }}>Este mês:</span>
                          <span className="font-semibold">{tenantDetails.statistics.officialApi.thisMonth.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span style={{ color: 'var(--text-secondary)' }}>Taxa de entrega:</span>
                          <span className="font-semibold text-blue-600">{tenantDetails.statistics.officialApi.deliveryRate.toFixed(1)}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span style={{ color: 'var(--text-secondary)' }}>Taxa de leitura:</span>
                          <span className="font-semibold text-purple-600">{tenantDetails.statistics.officialApi.readRate.toFixed(1)}%</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Timeline de Disparos */}
                  <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--bg-secondary)' }}>
                    <h3 className="text-lg font-bold mb-4" style={{ color: 'var(--text-primary)' }}>📈 Disparos (Últimos 30 dias)</h3>
                    {/* 🔧 v1.4.9: Gráfico temporariamente desativado (recharts não disponível) */}
                    <div className="flex items-center justify-center h-[250px] rounded border-2 border-dashed" style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)' }}>
                      <p style={{ color: 'var(--text-secondary)' }}>Gráfico temporariamente indisponível</p>
                    </div>
                    {/* 
                    <ResponsiveContainer width="100%" height={250}>
                      <LineChart data={tenantTimeline}>
                        <CartesianGrid strokeDasharray="3 3" stroke={'var(--border-color)'} />
                        <XAxis dataKey="date" tickFormatter={(value: string | number) => {
                          const date = new Date(value);
                          return `${date.getDate()}/${date.getMonth() + 1}`;
                        }} stroke={'var(--text-secondary)'} tick={{ fill: 'var(--text-secondary)' }} />
                        <YAxis stroke={'var(--text-secondary)'} tick={{ fill: 'var(--text-secondary)' }} />
                        <Tooltip contentStyle={{ backgroundColor: 'var(--bg-card)', border: `1px solid var(--border-color)`, color: 'var(--text-primary)' }} />
                        <Legend />
                        <Line type="monotone" dataKey="whatsappWeb" stroke="#3B82F6" name="WhatsApp Web" />
                        <Line type="monotone" dataKey="officialApi" stroke="#10B981" name="API Oficial" />
                      </LineChart>
                    </ResponsiveContainer>
                    */}
                  </div>

                  {/* Sessões WhatsApp Web */}
                  {tenantDetails.whatsappWebSessions.length > 0 && (
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-3">📱 Sessões WhatsApp Web</h3>
                      <div className="space-y-2">
                        {tenantDetails.whatsappWebSessions.map(session => (
                          <div key={session.id} className="rounded-lg p-3" style={{ border: '1px solid var(--border-color)' }}>
                            <div className="flex justify-between items-center">
                              <div>
                                <div className="font-medium">{session.sessionName}</div>
                                <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>{session.phoneNumber || 'Sem número'}</div>
                              </div>
                              <div className="text-right">
                                <div className={`text-sm font-semibold ${session.status === 'connected' ? 'text-green-600' : 'text-red-600'}`}>
                                  {session.status === 'connected' ? '✅ Conectado' : '❌ Desconectado'}
                                </div>
                                {session.connectedSince && (
                                  <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>há {session.connectedSince}</div>
                                )}
                                <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>{session.dispatchesToday} disparos hoje</div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Campanhas Ativas */}
                  {tenantDetails.campaigns.active.length > 0 && (
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-3">🚀 Campanhas Ativas</h3>
                      <div className="space-y-2">
                        {tenantDetails.campaigns.active.map(campaign => (
                          <div key={campaign.id} className="rounded-lg p-3" style={{ border: '1px solid var(--border-color)' }}>
                            <div className="flex justify-between items-center mb-2">
                              <div className="font-medium">{campaign.nome}</div>
                              <div className="text-sm">
                                <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                                  {campaign.campaignType}
                                </span>
                              </div>
                            </div>
                            {campaign.progress !== undefined && (
                              <div>
                                <div className="flex justify-between text-xs mb-1" style={{ color: 'var(--text-secondary)' }}>
                                  <span>{campaign.sent} / {campaign.totalContacts}</span>
                                  <span>{campaign.progress}%</span>
                                </div>
                                <div className="w-full rounded-full h-1.5" style={{ backgroundColor: 'var(--border-color)' }}>
                                  <div 
                                    className="bg-blue-600 h-1.5 rounded-full"
                                    style={{ width: `${campaign.progress}%` }}
                                  ></div>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12 text-red-600">
                  Erro ao carregar detalhes
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default SuperAdminTenantsPage;

